<template>
  <div id="history-filters" class="filters-container">
    <div v-if="appliedFilters && appliedFilters.length > 0" id="applied-filters" class="filter-row">
      <v-chip
        v-for="(f, index) in appliedFilters"
        :key="index"
        :disabled="isReset(f)"
        dark
        class="filters-chip elevation-2 mr-3 my-2"
        close
        close-icon="mdi-close"
        :title="$t(`device.filter.ids.${Object.keys(f)[0]}`)"
        @click:close="removeFilter(f)"
      >
        <span v-if="Object.keys(f)[0] === 'last_seen_seconds'" id="applied-filter-last_seen_seconds">{{ displayDiffDate(Object.values(f)[0]) }}</span>
        <span
          v-else-if="Object.keys(f)[0] === 'subCategories'"
          id="applied-filter-subCategories"
          class="ellipsis"
          :title="Object.values(f)[0]"
        >{{ Object.values(f)[0] }}</span>
        <span v-else-if="Object.keys(f)[0] === 'fromto'" id="applied-filter-fromto">{{ displayDate(Object.values(f)[0]) }}</span>
        <span v-else-if="Object.keys(f)[0] === 'variant_ids'" id="applied-filter-variants">{{ displayVariant(Object.values(f)[0]) }}</span>
        <span v-else :id="`applied-filter-${Object.keys(f)[0]}`">{{ Object.values(f)[0].join(", ") }}</span>
      </v-chip>
      <v-chip
        dark
        class="filters-reset elevation-2 mr-3 my-2"
        @click="resetFilters"
      >
        <img :src="require('@/assets/icons/filters/clear.svg')" alt="clear_filters">
        <span id="reset-filters">{{ $t('device.filter.reset_filters') }}</span>
      </v-chip>
      <AddBookmarkDialog :filters="appliedFilters" />
    </div>
    <div id="main-filters-container" class="filter-row">
      <FilterBookmarks @applyBookmark="applyBookmark" />
      <v-text-field
        id="filter-plate-id"
        v-model="multiSearch"
        :label="$t('device.filter.search_id_plate')"
        single-line
        outlined
        dark
        dense
        hide-details
        :color="colors.secondary"
        class="search-input"
        @keyup.enter.native="applyFilters"
      >
        <v-icon slot="append">mdi-magnify</v-icon>
      </v-text-field>
      <FilterItem
        filterid="device_types"
        translatekey="device_type"
        :suggestions="deviceTypes"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <FilterItem
        filterid="dtco_ids"
        translatekey="dtco_ids"
        :suggestions="{}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <FilterItem
        filterid="fromto"
        translatekey="date"
        :suggestions="{}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <v-btn
        id="toggle-advanced-filters"
        :title="$t('device.filter.more_filters')"
        dark
        text
        class="mr-2 px-2"
        @click="toggleMoreFilters"
      >
        <v-icon dark>mdi-tune</v-icon>
        <span class="mx-1 device-list-toolbar-btn">
          {{ displayMoreFilters ? $t('device.filter.less_filters') : $t('device.filter.more_filters') }}
        </span>
        <v-icon v-if="displayMoreFilters" dark>mdi-menu-up</v-icon>
        <v-icon v-else dark>mdi-menu-down</v-icon>
      </v-btn>
      <v-btn
        id="apply-filters"
        :title="$t('common.action.apply')"
        class="device-filters-btn ctp-btn mr-4 px-8"
        dark
        rounded
        @click="applyFilters"
      >
        <div class="apply_btn_content">
          <img :src="require('@/assets/icons/filters/apply.svg')" alt="apply_filters">
          <span>{{ $t('common.action.apply') }}</span>
        </div>
      </v-btn>
    </div>
    <div v-show="displayMoreFilters" id="advanced-filters-container" class="filter-row">
      <FilterItem
        :key="treeMenuKeyState"
        filterid="state"
        translatekey="state"
        :suggestions="getFilter('state')"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @addHistoryCat="addHistoryCat"
        @delHistoryCat="delHistoryCat"
      />
      <FilterItem
        :key="treeMenuKeyCmd"
        filterid="command"
        translatekey="command"
        :suggestions="getFilter('command')"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @addHistoryCat="addHistoryCat"
        @delHistoryCat="delHistoryCat"
      />
      <FilterItem
        :key="treeMenuKeyEvt"
        filterid="event"
        translatekey="event"
        :suggestions="getFilter('event')"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @addHistoryCat="addHistoryCat"
        @delHistoryCat="delHistoryCat"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, onMounted, ref } from 'vue';
import moment from 'moment';
import useStore from '@/utils/store/common';
import { INavigation } from '@/store/modules/devices/types';
import { FilterGroup } from 'ctp-filter-group-management';
import AddBookmarkDialog from '@/components/common/Filters/components/AddBookmarkDialog.vue';
import FilterBookmarks from '@/components/common/Filters/components/FilterBookmarks.vue';
import FilterItem from '@/components/common/Filters/components/FilterItem.vue';
import { colors } from '@/constants/theme/colors';
import './styles/history-filter.scss';

const store = useStore();

const props = defineProps({
  redirectionID: {
    type: String,
    default: ''
  }
});

const deviceTypes = { device_types: ['obu5gen', 'vdolink'] };

const getNavigationConfiguration: ComputedRef<INavigation> = computed(() => store.getters['history/getNavigationConfiguration']);
const getFiltersMeta: ComputedRef<any> = computed(() => store.getters['history/getFiltersMeta']);
const displayDiffDate = (seconds: number) => moment().subtract(seconds, 'seconds').format('DD/MM/YYYY');
const displayDate = (timestamp: number) => moment.unix(timestamp).format('DD/MM/YYYY');

const displayMoreFilters = ref(false);
const activeFiltersCount = ref(0);
const appliedFilters = ref<{ [key: string]: any }[]>([]);
const selectedFilters = ref<{ id: string, values: string[] }[]>([]);
const multiSearch = ref('');
const subCategories = ref<string[]>([]);
const resetedFilters = ref<string[]>([]);
const resetMe = ref('');
const reset = ref(false);

// Used to reset all filters with a TreeMenu component. FilterItem has a :key that resets the component when changed
const treeMenuKeyState = ref(0);
const treeMenuKeyCmd = ref(1);
const treeMenuKeyEvt = ref(2);

function getFilter(name: string) {
  const data = getFiltersMeta.value.find((f: { name: string; }) => f.name === name);
  if (data) {
    return { ...data };
  }
  return {};
}

function applyFilters(fetchHistory = true) {
  const filters: {[key: string]: any} = {};
  resetMe.value = '';
  resetedFilters.value = [];
  // Push all selected filters
  if (selectedFilters.value.length > 0) {
    selectedFilters.value.forEach((item) => {
      filters[item.id] = item.values;
    });
  }
  // Push subCategories filters
  if (subCategories.value.length > 0) {
    filters.subCategories = subCategories.value.join();
  }
  // Push multisearch bar values
  if (multiSearch.value !== '') {
    activeFiltersCount.value = 1;
    filters.identifierMatchers = multiSearch.value.replace(/ /g, '').split(',');
  }
  const newNavigationConfiguration = {
    ...getNavigationConfiguration
  };
  newNavigationConfiguration.value.filters = filters;
  newNavigationConfiguration.value.start = 0;
  appliedFilters.value = Object.entries(filters).map(([key, value]) => ({ [key]: value }));
  if (fetchHistory) store.dispatch('history/fetchHistory', { ...newNavigationConfiguration.value });
}

onMounted(async () => {
  await store.dispatch('history/fetchFiltersMeta');
  if (props.redirectionID && props.redirectionID !== '') {
    multiSearch.value = props.redirectionID;
    applyFilters();
  }
});

function resetFilterValues() {
  selectedFilters.value = [];
  multiSearch.value = '';
  subCategories.value = [];
  activeFiltersCount.value = 0;
  appliedFilters.value = [];
  resetedFilters.value = [];
  reset.value = true;
  treeMenuKeyState.value++;
  treeMenuKeyCmd.value++;
  treeMenuKeyEvt.value++;
}

function resetFilters() {
  resetFilterValues();
  const newNavigationConfiguration = {
    ...getNavigationConfiguration
  };
  newNavigationConfiguration.value.filters = {};
  newNavigationConfiguration.value.start = 0;
  store.dispatch('history/updateNavigation', { ...newNavigationConfiguration.value });
}

function updateFilters(key: string, val: string[]) {
  const index = selectedFilters.value.findIndex(item => item.id === key);
  if (index !== -1) {
    selectedFilters.value[index].values = val;
  } else {
    selectedFilters.value.push({ id: key, values: val });
  }
}

const triggerReset = (filterID: string) => {
  resetMe.value = filterID;
  resetedFilters.value.push(filterID);
};

function removeFilter(f: any) {
  if (f.identifierMatchers) {
    multiSearch.value = '';
  } else if (f.subCategories) {
    subCategories.value = [];
    treeMenuKeyState.value++;
    treeMenuKeyCmd.value++;
    treeMenuKeyEvt.value++;
  } else {
    selectedFilters.value = selectedFilters.value.filter(item => item.id !== Object.keys(f)[0]); // enlève les chip
  }
  triggerReset(Object.keys(f)[0]);
}

function applyBookmark(bookmark: FilterGroup) {
  resetFilterValues();
  Object.entries(JSON.parse(bookmark.filters)).forEach(([key, values]: any) => {
    if (key === 'device_ids') {
      multiSearch.value = values.join(',');
    } else {
      selectedFilters.value.push({ id: key, values });
    }
  });
  applyFilters();
}

const isReset = computed(() => (f: any) => resetedFilters.value.includes(Object.keys(f)[0]));

function toggleMoreFilters() {
  displayMoreFilters.value = !displayMoreFilters.value;
}

function addHistoryCat(cat: string) {
  subCategories.value.push(cat);
}

function delHistoryCat(cat: string) {
  subCategories.value = subCategories?.value.filter(item => item !== cat);
}
</script>
